import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Job } from '@app/models/job.model';
import { FileStorageInfo } from '@app/models/commonfilestorage.model';
import { CommonFileStorageService } from '@app/services/commonfilestorage/commonfilestorage.service';
import { SiteApiService } from '@app/services/site/site.api.service';
import { Company, UserCompany } from '@app/models/user.model';
import { ClosedownService } from '@app/services/closedown/closedown.service';
import { JobService } from '@app/services/job/job.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { UserService } from '@app/services/user/user.service';
import { CustomValidators } from '@app/shared/custom-validators';
import { AllocationMode, AllocationType, JobStatus, JobType, OpenJobsListType } from '@app/shared/enums';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, map, of, Subject, switchMap, tap, throwError } from 'rxjs';
import { LocalizationService } from '../internationalization/localization.service';


@Component({
  selector: 'app-open-jobs-list',
  templateUrl: './open-jobs-list.component.html',
  styleUrls: ['./open-jobs-list.component.scss'],
  standalone: false
})
export class OpenJobsListComponent implements OnInit, OnDestroy {
  readonly AllocationMode = AllocationMode;
  jobs: Job[] = [];
  siteId = '';
  siteName = '';
  jobType = '';
  selectedSubSite = '';
  allocationType = '';
  hasLoaded = false;
  hasLoadError = false;
  selectedJob?: Job;
  allocationMode = AllocationMode.INTERNAL;
  allocation = {
    email: '',
    companyName: '',
    sendWorkOrder: false
  };
  subconCompanies: Company[] = [];
  isLoadingCompanies = false;
  hasSearchedCompanies = false;
  isAllocating = false;
  allocationErrorKey = '';
  photosByJob: Record<string, FileStorageInfo[]> = {};
  expandedPhotoJobs = new Set<string>();
  isLoadingPictures = false;
  hasPicturesError = false;
  // Hidden until work order downloads are configured.
  readonly showWorkOrderDownload = false;

  private readonly fileStorageService = inject(CommonFileStorageService);
  private readonly localizationService = inject(LocalizationService);
  private readonly siteApiService = inject(SiteApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly emailChanges = new Subject<string>();
  private readonly emailValidator = CustomValidators.isEmailValid();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly jobService: JobService,
    private readonly userService: UserService,
    private readonly closedownService: ClosedownService,
    private readonly navigationService: NavigationService
  ) {
    this.navigationService.onChange(false, false);
    this.watchExternalEmailChanges();
  }

  get isPpm(): boolean {
    return this.jobType.toUpperCase() === JobType.PPM_JOB;
  }

  get jobTypeKey(): string {
    const keys: Record<string, string> = {
      [OpenJobsListType.NEW]: 'open_jobs_summary_new',
      [OpenJobsListType.ON_SITE]: 'open_jobs_summary_on_site',
      [OpenJobsListType.RETURNING]: 'open_jobs_summary_returning',
      [OpenJobsListType.AWAITING_PARTS]: 'open_jobs_summary_awaiting_parts',
      [JobType.PPM_JOB]: 'open_jobs_summary_ppm'
    };

    return keys[this.jobType.toUpperCase()] || 'open_jobs_summary_title';
  }

  get subSites(): string[] {
    return [...new Set(this.jobs.map(job => job.subSite).filter(Boolean))];
  }

  get filteredJobs(): Job[] {
    return this.jobs.filter(job =>
      this.matchesJobType(job) &&
      (!this.selectedSubSite || job.subSite === this.selectedSubSite) &&
      (!this.allocationType ||
        (this.allocationType === 'allocated' ? Boolean(job.allocatedUserEmail) : !job.allocatedUserEmail))
    );
  }

  get hasVisiblePictures(): boolean {
    return this.filteredJobs.some(job => this.photosByJob[job.id]?.length > 0);
  }

  get isExternalAllocation(): boolean {
    return this.allocationMode === AllocationMode.EXTERNAL;
  }

  get canConfirmAllocation(): boolean {
    if (!this.isEmailValid(this.allocation.email)) {
      return false;
    }

    return !this.isExternalAllocation || this.allocation.companyName.trim().length >= 5;
  }

  get filteredSubconCompanies(): Company[] {
    const typedName = this.allocation.companyName.trim().toLowerCase();
    if (!typedName) {
      return this.subconCompanies;
    }

    return this.subconCompanies.filter(company => company.name?.toLowerCase().includes(typedName));
  }

  get hasNoSubconCompanies(): boolean {
    return this.isExternalAllocation && this.hasSearchedCompanies && !this.isLoadingCompanies && this.subconCompanies.length === 0;
  }

  ngOnInit(): void {
    this.siteId = this.route.snapshot.paramMap.get('siteId') || '';
    this.jobType = this.route.snapshot.paramMap.get('jobType') || '';
    this.siteName = this.route.snapshot.queryParamMap.get('siteName') || '';

    if (!this.siteId || !this.jobType) {
      this.hasLoadError = true;
      this.hasLoaded = true;
      return;
    }

    this.loadJobs();
  }

  private loadJobs(): void {
    this.hasLoaded = false;
    this.hasLoadError = false;
    this.jobService.getResourceAdminJobsBySiteId(this.siteId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: jobs => {
        this.jobs = jobs;
        this.siteName = this.siteName || jobs[0]?.site?.name || '';
        this.hasLoaded = true;
        if (!this.isPpm) {
          this.loadPictures();
        }
      },
      error: () => {
        this.hasLoadError = true;
        this.hasLoaded = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.navigationService.onChange(true, false);
  }

  loadPictures(): void {
    this.photosByJob = {};
    this.expandedPhotoJobs = new Set();
    this.hasPicturesError = false;
    this.isLoadingPictures = false;
    const references = [...new Set(this.jobs.map(job => job.calloutReferenceNumber).filter(Boolean))];
    if (references.length === 0) {
      return;
    }

    const brandReference = this.jobs[0]?.site?.brand?.referenceNumber;
    if (!brandReference) {
      this.hasPicturesError = true;
      return;
    }

    this.isLoadingPictures = true;
    this.siteApiService.getBrandByReferenceNumber(brandReference).pipe(
      switchMap(brand => {
        const partnerId = brand?.partner?.referenceNumber;
        if (!partnerId) {
          return throwError(() => new Error('Picture lookup requires a partner reference.'));
        }

        const requests = [];
        for (let index = 0; index < references.length; index += 50) {
          requests.push(this.fileStorageService.GetFileStorageInfosByReferenceTypeAndIds(
            'JobEvent', references.slice(index, index + 50).join(','), partnerId
          ));
        }
        return forkJoin(requests);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: batches => {
        const photosByReference = new Map<string, FileStorageInfo[]>();
        for (const photo of batches.flat()) {
          if (photo.fileUploadTypeCode !== 'UD' || !photo.referenceId) {
            continue;
          }
          const photos = photosByReference.get(photo.referenceId) ?? [];
          photos.push(photo);
          photosByReference.set(photo.referenceId, photos);
        }
        this.photosByJob = Object.fromEntries(this.jobs.map(job =>
          [job.id, photosByReference.get(job.calloutReferenceNumber) ?? []]
        ));
        this.isLoadingPictures = false;
      },
      error: () => {
        this.isLoadingPictures = false;
        this.hasPicturesError = true;
      }
    });
  }

  togglePictures(jobId: string): void {
    const expanded = new Set(this.expandedPhotoJobs);
    if (expanded.has(jobId)) {
      expanded.delete(jobId);
    } else {
      expanded.add(jobId);
    }
    this.expandedPhotoJobs = expanded;
  }

  openJob(job: Job): void {
    if (this.isPpm) {
      void this.closedownService.directToClosedown(false, job.companyReferenceNumber);
      return;
    }

    void this.router.navigate(
      ['openjobs', this.siteId, this.jobType, job.id],
      { queryParams: { siteName: this.siteName } }
    );
  }

  openAllocateTo(job: Job): void {
    this.selectedJob = job;
    this.allocationMode = AllocationMode.INTERNAL;
    this.resetAllocationFields();
  }

  closeAllocateTo(): void {
    if (!this.isAllocating) {
      this.selectedJob = undefined;
      this.allocationErrorKey = '';
    }
  }

  changeAllocationMode(): void {
    this.resetAllocationFields();
  }

  changeAllocationEmail(): void {
    if (this.isExternalAllocation) {
      this.emailChanges.next(this.allocation.email);
    }
  }

  confirmAllocation(): void {
    if (!this.selectedJob || !this.canConfirmAllocation || this.isAllocating) {
      return;
    }

    const selectedJob = this.selectedJob;
    const emailAddress = this.allocation.email.trim();
    const userCompany: UserCompany = {
      adminCompanyId: this.selectedJob?.companyId,
      emailAddress: emailAddress,
      allocationType: this.isExternalAllocation ? AllocationType.EXTERNAL : AllocationType.INTERNAL,
      externalCompanyName: this.isExternalAllocation ? this.allocation.companyName.trim() : undefined
    };

    this.isAllocating = true;
    this.allocationErrorKey = '';
    this.userService.createUserCompany(userCompany).pipe(
      switchMap(createdUserCompany => {
        if (!createdUserCompany?.userId || !createdUserCompany?.adminCompanyId) {
          return of(false);
        }

        const allocateCompanyId = createdUserCompany.adminCompanyId;
        return this.jobService.jobAllocation({
          jobId: selectedJob.id,
          allocatedUserEmail: createdUserCompany.emailAddress ?? emailAddress,
          allocationType: this.isExternalAllocation ? AllocationType.EXTERNAL : AllocationType.INTERNAL,
          companyId: allocateCompanyId
        }).pipe(
          tap(allocated => {
            if (allocated) {
              selectedJob.allocateCompanyId = allocateCompanyId;
            }
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: allocated => {
        this.isAllocating = false;
        if (!allocated) {
          this.allocationErrorKey = 'open_jobs_list_allocate_error';
          return;
        }

        selectedJob.allocatedUserEmail = emailAddress;
        selectedJob.allocatedCompanyName = null;
        this.selectedJob = undefined;
        this.loadJobs();
        alert(this.localizationService.translate('open_jobs_list_allocate_success'));
      },
      error: () => {
        this.isAllocating = false;
        this.allocationErrorKey = 'open_jobs_list_allocate_error';
      }
    });
  }

  canResendJobDetails(job: Job): boolean {
    const status = job.jobStatusName?.replaceAll('-', '').replaceAll(' ', '').toUpperCase();
    return !!job.allocatedUserEmail && status === JobStatus.CALLOUT.replaceAll('-', '').replaceAll(' ', '').toUpperCase();
  }

  resendJobDetails(): void {
    if (confirm(this.localizationService.translate('open_jobs_list_resend_job_details_confirm'))) {
      alert(this.localizationService.translate('open_jobs_list_resend_job_details_unavailable'));
    }
  }

  // TODO: connect to the work order download API once configured.
  downloadWorkOrder(_job: Job): void {}

  private watchExternalEmailChanges(): void {
    this.emailChanges.pipe(
      map(email => email.trim()),
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => {
        this.allocation.companyName = '';
        this.subconCompanies = [];
        this.hasSearchedCompanies = false;
      }),
      switchMap(email => {
        if (!this.isExternalAllocation || !this.isEmailValid(email)) {
          return of<Company[]>([]);
        }

        this.isLoadingCompanies = true;
        return this.userService.getUserSubconCompaniesByEmail(email).pipe(
          map(companies => companies ?? []),
          tap(() => this.hasSearchedCompanies = true),
          catchError(() => {
            this.hasSearchedCompanies = true;
            return of<Company[]>([]);
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(companies => {
      this.isLoadingCompanies = false;
      this.subconCompanies = companies;
    });
  }

  private isEmailValid(email: string): boolean {
    return this.emailValidator(new FormControl(email.trim())) === null;
  }

  private resetAllocationFields(): void {
    this.allocation = {
      email: '',
      companyName: '',
      sendWorkOrder: false
    };
    this.subconCompanies = [];
    this.isLoadingCompanies = false;
    this.hasSearchedCompanies = false;
    this.allocationErrorKey = '';
  }

  private matchesJobType(job: Job): boolean {
    if (this.isPpm) {
      return job.jobType?.toUpperCase() === JobType.PPM_JOB;
    }

    if (job.jobType?.toUpperCase() === JobType.PPM_JOB) {
      return false;
    }

    const statusesByJobType: Record<string, string[]> = {
      [OpenJobsListType.NEW]: [JobStatus.CALLOUT],
      [OpenJobsListType.ON_SITE]: [JobStatus.ONSITE],
      [OpenJobsListType.RETURNING]: [JobStatus.RETURNING],
      [OpenJobsListType.AWAITING_PARTS]: [JobStatus.AWAITING_PARTS]
    };
    const status = job.jobStatusName?.replaceAll('-', '').replaceAll(' ', '').toUpperCase();
    return statusesByJobType[this.jobType.toUpperCase()]?.some(jobStatus =>
      jobStatus.replaceAll('-', '').replaceAll(' ', '').toUpperCase() === status
    ) ?? false;
  }
}